import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { BookOpen, Compass, Loader2, ArrowRight, Bookmark, Link2 } from "lucide-react";
import ConceptEssayDialog from "./ConceptEssayDialog";

/**
 * ProgressJournal — a warm "concepts you've explored" journal for the
 * Dashboard.  No XP, no gems, no streak flames.  Just: which of the
 * twelve worlds have you touched, how many chapters have you read /
 * saved, what did you read most recently.
 */
export default function ProgressJournal() {
  const [data, setData] = useState(null);
  const [essay, setEssay] = useState(null);   // { term, worlds } | null

  useEffect(() => {
    api.get("/progress/journal/me")
      .then(({ data }) => setData(data))
      .catch(() => setData({ worlds: [], totals: {}, recent_reads: [], recent_saves: [] }));
  }, []);

  if (!data) {
    return <div className="rounded-2xl border-2 border-ink bg-cream p-6 text-ink2 text-sm" data-testid="progress-journal-loading">
      <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Opening your journal…
    </div>;
  }

  const totals = data.totals || {};
  const touchedWorlds = (data.worlds || []).filter((w) => w.touched);
  const untouchedWorlds = (data.worlds || []).filter((w) => !w.touched);

  return (
    <section className="rounded-3xl border-2 border-ink bg-cream shadow-[8px_8px_0_0_#1F1A17] overflow-hidden" data-testid="progress-journal">
      <header className="p-6 md:p-7 border-b-2 border-ink flex items-start gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-2xl bg-marigold/25 text-marigold flex items-center justify-center flex-shrink-0">
          <BookOpen className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-[260px]">
          <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-marigold mb-1">Your journal</div>
          <h3 className="font-heading text-2xl font-semibold text-ink leading-tight">Concepts you've explored</h3>
          <p className="text-sm text-ink2 mt-1">
            Not a scoreboard — just an honest map of where you've been in the book.
          </p>
        </div>
      </header>

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 border-b-2 border-ink" data-testid="progress-journal-totals">
        <Totem label="Chapters read"   value={totals.chapters_read || 0} />
        <Totem label="Chapters saved"  value={totals.chapters_saved || 0} border />
        <Totem label="Worlds touched"  value={`${totals.worlds_touched || 0} / 12`} border />
        <Totem label="Road-trip stops" value={`${totals.stops_completed || 0} / 12`} border />
      </div>

      {/* Worlds explored */}
      <div className="p-6 md:p-7 border-b-2 border-ink">
        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-ink2/70 mb-3">Where you've been</div>
        {touchedWorlds.length === 0 ? (
          <div className="text-ink2 text-sm italic" data-testid="progress-journal-empty">
            You haven't opened a chapter yet.  <Link to="/learn" className="text-marigold underline">Pick a world to begin.</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {touchedWorlds.map((w) => (
              <Link
                key={w.slug}
                to={`/learn/${w.slug}`}
                className="group block rounded-2xl border border-ink/25 hover:border-ink hover:-translate-y-0.5 hover:shadow-[4px_4px_0_0_#1F1A17] transition-all p-4"
                data-testid={`progress-journal-world-${w.slug}`}
              >
                <div className="text-[9px] uppercase tracking-[0.24em] font-bold text-marigold mb-1">{w.kicker}</div>
                <div className="font-heading text-base font-semibold text-ink group-hover:text-marigold transition mb-2 leading-tight">{w.title}</div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 h-2 rounded-full bg-ink/10 overflow-hidden">
                    <div className="h-full bg-marigold transition-all" style={{ width: `${Math.min(100, w.pct)}%` }} />
                  </div>
                  <span className="text-[10px] font-mono text-ink2 flex-shrink-0">{w.read}/{w.total}</span>
                </div>
                <div className="text-[11px] text-ink2 flex items-center gap-3">
                  {w.saved > 0 && <span className="inline-flex items-center gap-1"><Bookmark className="w-3 h-3" /> {w.saved} saved</span>}
                </div>
              </Link>
            ))}
          </div>
        )}

        {untouchedWorlds.length > 0 && (
          <details className="mt-4 group" data-testid="progress-journal-untouched">
            <summary className="cursor-pointer text-xs text-ink2 hover:text-marigold uppercase tracking-[0.2em] font-bold inline-flex items-center gap-1.5">
              <Compass className="w-3.5 h-3.5" /> {untouchedWorlds.length} worlds still ahead
            </summary>
            <div className="mt-3 flex flex-wrap gap-2">
              {untouchedWorlds.map((w) => (
                <Link
                  key={w.slug}
                  to={`/learn/${w.slug}`}
                  className="text-xs px-3 py-1.5 rounded-full border border-ink/25 hover:border-marigold hover:text-marigold transition"
                  data-testid={`progress-journal-untouched-${w.slug}`}
                >
                  {w.title}
                </Link>
              ))}
            </div>
          </details>
        )}
      </div>

      {/* Cross-world concepts — the "seen in more than one world" list */}
      {data.cross_world_concepts?.length > 0 && (
        <div className="p-6 md:p-7 border-b-2 border-ink bg-marigold/[0.04]" data-testid="progress-journal-cross-world">
          <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-[0.28em] font-bold text-marigold">
            <Link2 className="w-3.5 h-3.5" /> Concepts you've seen across worlds
          </div>
          <p className="text-sm text-ink2 mb-4 italic max-w-xl">
            An idea shows up here when your reading has touched it in more than one place in the book.
            That's what "learning" looks like from above.
          </p>
          <ul className="flex flex-wrap gap-2" data-testid="progress-journal-cross-world-list">
            {data.cross_world_concepts.map((c) => (
              <li key={c.term}>
                <button
                  type="button"
                  onClick={() => setEssay({ term: c.term, worlds: c.worlds })}
                  className="group rounded-2xl border-2 border-ink bg-cream shadow-[3px_3px_0_0_#1F1A17] hover:shadow-[5px_5px_0_0_#1F1A17] hover:-translate-y-0.5 transition-all text-left px-3.5 py-2 flex items-center gap-2"
                  data-testid={`progress-journal-cross-world-${c.term}`}
                >
                  <span className="font-heading font-semibold text-ink group-hover:text-marigold transition capitalize text-sm">{c.term}</span>
                  <span className="text-[10px] font-mono text-ink2/70">
                    · {c.worlds.length} worlds · {c.chapter_count} chapters
                  </span>
                  <ArrowRight className="w-3 h-3 text-ink/40 group-hover:text-marigold group-hover:translate-x-0.5 transition-transform" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recent reads */}
      {data.recent_reads?.length > 0 && (
        <div className="p-6 md:p-7">
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-ink2/70 mb-3">Recently read</div>
          <ol className="space-y-2" data-testid="progress-journal-recent">
            {data.recent_reads.slice(0, 5).map((r) => (
              <li key={r.slug}>
                <Link
                  to={`/library/${r.slug}`}
                  className="group flex items-start gap-3 hover:bg-marigold/[0.06] p-2 rounded-lg transition"
                  data-testid={`progress-journal-recent-${r.code || r.slug}`}
                >
                  <div className="text-[10px] font-mono text-ink/50 pt-1 whitespace-nowrap">{r.code}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-heading font-semibold text-ink group-hover:text-marigold transition truncate">{r.title}</div>
                    {r.summary && <div className="text-xs text-ink2 line-clamp-1">{r.summary}</div>}
                  </div>
                  <ArrowRight className="w-4 h-4 text-ink/40 group-hover:text-marigold flex-shrink-0" />
                </Link>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Concept essay dialog — mounts only when a pill is clicked */}
      {essay && (
        <ConceptEssayDialog
          term={essay.term}
          worlds={essay.worlds}
          onClose={() => setEssay(null)}
        />
      )}
    </section>
  );
}

function Totem({ label, value, border }) {
  return (
    <div className={`px-5 py-4 ${border ? "border-l-2 border-ink/25" : ""}`}>
      <div className="font-heading text-3xl md:text-4xl font-semibold text-ink leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink2/70 mt-2">{label}</div>
    </div>
  );
}
